import { Checkbox } from '@material-ui/core';
import { T } from 'components/common/text';
import CustomText from 'components/common/text/CustomText';
import { checkError } from 'lib/clinic/library';
import { indicationOptionList } from 'lib/clinic/mapper';
import { ICreateOptionValue, IOptionUi } from 'lib/types/clinic/projectTypes';
import { TValue } from 'lib/types/common';
import React, { MouseEventHandler, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { _color } from 'styles/_variables';

interface ProjectOptionCheckboxProps {
  type: 'default' | 'advanced';
  optionValues: ICreateOptionValue;
  isPhotogrammetry?: boolean;
  isSituScanDisabled?: {
    upperJaw: boolean;
    lowerJaw: boolean;
  };
  groupItem: {
    title: string;
    list: {
      key: string;
      title: string;
    }[];
    isPhotogrammetry: boolean;
  };
  onCheckChange: (
    type: 'default' | 'advanced',
    optionKey: string,
    value: TValue,
    isPhotogrammetry: boolean,
  ) => void;
}

interface OptionCheckbox {
  option: IOptionUi;
  checked: boolean;
  disabled: boolean;
  optionLabel: string;
  onClick: MouseEventHandler<HTMLSpanElement>;
}

const ProjectOptionCheckbox = ({
  type,
  optionValues,
  groupItem,
  isPhotogrammetry = false,
  isSituScanDisabled,
  onCheckChange,
}: ProjectOptionCheckboxProps) => {
  return (
    <StyledProjectOptionCheckbox>
      <div className="projectOption__title">
        <CustomText fontSize={20} fontWeight={600} fontColor="#222">
          <T>{groupItem.title}</T>
        </CustomText>
      </div>
      <div className="projectOption__content">
        {useMemo(
          () =>
            !!groupItem &&
            groupItem.list.map((option: IOptionUi, index: number) => {
              const { key: optionKey, title: optionLabel } = option;
              const optionValue = optionValues[optionKey]?.value;
              const checked = !!optionValue;
              let disabled = false;

              if (optionKey === 'situScanUpper') {
                disabled = !isSituScanDisabled?.upperJaw;
              }
              if (optionKey === 'situScanLower') {
                disabled = !isSituScanDisabled?.lowerJaw;
              }

              if (!isPhotogrammetry && checkError(optionValue)) return;

              return (
                <OptionCheckbox
                  key={index}
                  option={option}
                  checked={checked}
                  disabled={disabled}
                  optionLabel={optionLabel}
                  onClick={() => onCheckChange(type, optionKey, checked, isPhotogrammetry)}
                />
              );
            }),
          [type, groupItem, optionValues, isSituScanDisabled, isPhotogrammetry],
        )}
      </div>
    </StyledProjectOptionCheckbox>
  );
};

const OptionCheckbox = ({ option, onClick, checked, disabled, optionLabel }: OptionCheckbox) => {
  // SECTION: declare hooks
  const { t } = useTranslation();

  return (
    <span
      key={option.key}
      className="option_checkbox"
      onClick={e => {
        !disabled && onClick(e);
      }}
    >
      <Checkbox
        checked={checked}
        className="checkbox_control_checkbox"
        color="primary"
        icon={<div className="custom_checkbox"></div>}
      />
      <CustomText fontSize={16} fontWeight={400} fontColor={disabled ? '#888' : '#444'}>
        {t(optionLabel)}
      </CustomText>
    </span>
  );
};

const StyledProjectOptionCheckbox = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 20px;
  .projectOption__title {
    width: 163px;
    display: flex;
    align-items: center;
  }
  .projectOption__content {
    display: flex;
    column-gap: 30px;
    padding: 10px 0;
    .option_checkbox {
      display: flex;
      column-gap: 10px;
      align-items: center;
      cursor: pointer;
      .custom_checkbox {
        width: 18px;
        height: 18px;
        border: 1px solid #e5e7ed;
        border-radius: 4px;
      }
      .MuiCheckbox-root {
        padding: 0;
        .MuiIconButton-label {
          width: 20px;
          height: 20px;
          color: ${_color.blue};
        }
      }
    }
  }
`;

export default ProjectOptionCheckbox;
